import React, { useCallback, useContext } from 'react'

import {
  Container,
  DatesVariant,
  ImageContainer,
  ImageOverlayContainer,
  renderImage,
} from './Common'

import HeightFillingImage from 'components/Common/HeightFillingImage'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import offerPageURL from 'lib/offer/offerPageURL'
import TripItemSummary from 'tripPlanner/components/TripModal/TripItems/Summary'
import TripContext, { useTripId } from 'tripPlanner/contexts/TripContext'
import { useOffers } from 'tripPlanner/hooks/api'
import useImmersiveModeHandlers from 'tripPlanner/hooks/useImmersiveModeHandlers'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { TourItem } from 'tripPlanner/types/tripItem'
import { isCustomItem } from 'tripPlanner/utils'
import { itemSectionId } from 'tripPlanner/utils/itemSection'

interface Props {
  item: TourItem
}

interface LuxProps extends Props {
  offer: Tours.V2Offer | App.Offer
}

function LuxTour({ item, offer }: LuxProps) {
  const {
    startPlace,
    isBooked,
    name,
    type,
    startDate,
    endDate,
    startDay,
    endDay,
  } = item

  const openModal = useOpenModal()
  const { setImmersiveMode } = useImmersiveModeHandlers()
  const tripId = useTripId()

  const offerUrl = offerPageURL(offer)

  const tripContext = useContext(TripContext)
  const canEdit = tripContext?.permissions.canEdit ?? false

  const handleClick = useCallback(() => {
    if (isBooked) {
      openModal(TripItemSummary, { item })
    }
    if (canEdit) {
      setImmersiveMode({ tripId, sectionId: itemSectionId(item.id) })
    }
  }, [canEdit, isBooked, item, openModal, setImmersiveMode, tripId])

  return (
    <>
      {offer?.image?.id && (
        <ImageContainer>
          {offer.image.id && <HeightFillingImage id={offer.image.id} />}
          <ImageOverlayContainer>
            <ProductTypeLabel productType={offer.productType} />
          </ImageOverlayContainer>
        </ImageContainer>
      )}
      <DatesVariant
        region={startPlace.region}
        name={name}
        startDate={startDate}
        endDate={endDate}
        type={type}
        isBooked={isBooked}
        onClick={handleClick}
        to={isBooked ? undefined : offerUrl}
        linksToOffer={!isBooked}
        canEdit={canEdit && !isBooked}
        startDay={startDay}
        endDay={endDay}
      />
    </>
  )
}

function CustomTour({ item }: Props) {
  const {
    isBooked,
    startPlace,
    name,
    type,
    startDate,
    endDate,
    startDay,
    endDay,
  } = item

  const openModal = useOpenModal()

  const onClick = () => openModal(TripItemSummary, { item })
  const tripContext = useContext(TripContext)
  const canEdit = tripContext?.permissions.canEdit ?? false

  return (
    <>
      {renderImage(startPlace)}
      <DatesVariant
        region={startPlace.region}
        name={name}
        startDate={startDate}
        endDate={endDate}
        type={type}
        isBooked={isBooked}
        onClick={onClick}
        canEdit={canEdit && !isBooked}
        startDay={startDay}
        endDay={endDay}
      />
    </>
  )
}

const TourSummaryCard = React.forwardRef<HTMLDivElement, Props>(
  ({ item }: Props, ref) => {
    const { sourceType, code } = item

    const { data: offers, isLoading } = useOffers(code ? [code] : [])

    const offer = offers?.[0] as App.Offer | Tours.V2Offer

    return (
      <Container ref={ref}>
        {isCustomItem(item) && <CustomTour item={item} />}
        {sourceType === 'LE_Tour' && offer && (
          <LuxTour item={item} offer={offer} />
        )}
        {isLoading && sourceType === 'LE_Tour' && <LoadingIndicator inline />}
        {!isLoading && sourceType === 'LE_Tour' && !offer && (
          <>Could not load offer.</>
        )}
      </Container>
    )
  },
)

export default TourSummaryCard
